import { useMemo, useState } from 'react';
import { ProLock, UpgradePrompt } from '../license/UpgradePrompt';
import { driftRateLabel, formatTimecode, parseTimecode } from '../editor/metrics';
import { applyResync } from '../resync/engine';
import { workerRunner } from '../resync/workerRunner';
import type { ResyncRunner } from '../resync/types';
import {
  convertFrameRate,
  driftFromSyncPoints,
  linearScale,
  shiftCues,
  STANDARD_FPS,
  type AlignmentResult,
  type Anchor,
  type SubtitleDocument,
} from '../../core';
import { plural } from '../text';
import { track } from '../analytics/client';
import './TimingControls.css';

/**
 * The timing-correction tools, extracted from the Sync route (cue-forge-gfw) so
 * they live in Studio too: retiming is a tool that acts on the loaded document,
 * not a separate mode. Shift + linear retime are free (table stakes,
 * cue-forge-5dv.2); frame-rate conversion, drift correction, and auto-resync are
 * Pro, rendered visible-but-locked for free users (spec §7.5) — the controls gate
 * inline, not as a whole Pro route. Every transform goes through the caller's
 * undoable setDoc, so undo/redo covers retiming alongside edits and QC fixes.
 * Works with no video loaded — the alignment tools attach their own audio file.
 */
export function TimingControls({
  doc,
  onApply,
  isPro,
  resyncRunner = workerRunner,
}: {
  doc: SubtitleDocument;
  onApply: (doc: SubtitleDocument) => void;
  isPro: boolean;
  resyncRunner?: ResyncRunner;
}) {
  // ONE local status line for the whole Timing panel (cue-forge-w28.13): every
  // applied operation used to succeed silently. Intent-only — set exclusively by
  // the user's Apply/Shift/Convert clicks — and deliberately its own surface:
  // QC announcements stay in the Inspector's single live region (drt.18).
  const [status, setStatus] = useState('');
  function apply(next: SubtitleDocument, message: string) {
    onApply(next);
    setStatus(message);
  }

  return (
    <div className="cf-timing">
      {/* Free-vs-Pro orientation, carried over from the old Sync route
          (cue-forge-zxw.11) — lost when Sync folded into the Studio Timing panel. */}
      <p className="cf-timing__hint">
        Shift and linear retime are free; frame-rate conversion, drift correction, and auto-resync
        are Pro.
      </p>
      <ShiftTool doc={doc} onApply={apply} />
      <RetimeTool doc={doc} onApply={apply} />

      <FrameRateTool doc={doc} onApply={apply} isPro={isPro} />
      <DriftTool doc={doc} onApply={apply} isPro={isPro} />
      <AutoResyncTool doc={doc} onApply={apply} isPro={isPro} runner={resyncRunner} />

      {!isPro && <UpgradePrompt feature="Advanced sync" surface="timing" compact />}

      {/* Always-mounted polite live region: confirms the last applied operation. */}
      <p className="cf-timing__status" role="status" aria-live="polite">
        {status}
      </p>
    </div>
  );
}

interface ToolProps {
  doc: SubtitleDocument;
  /** Apply a transformed doc plus the confirmation to show in the status line. */
  onApply: (doc: SubtitleDocument, message: string) => void;
}

/** "1 cue" / "42 cues" for the status-line confirmations. */
function cueCount(doc: SubtitleDocument): string {
  return plural(doc.cues.length, 'cue');
}

/** Parse a shift magnitude: a timecode (HH:MM:SS,mmm) or a plain ms count. */
function parseOffset(input: string): number | null {
  const tc = parseTimecode(input);
  if (tc !== null) return tc;
  const m = /^(\d+)\s*(?:ms)?$/.exec(input.trim());
  return m ? Number(m[1]) : null;
}

/** Shift every cue earlier/later by a fixed offset (free) — engine: shiftCues. */
function ShiftTool({ doc, onApply }: ToolProps) {
  const [offset, setOffset] = useState('00:00:01,000');
  const [error, setError] = useState<string | null>(null);

  function apply(sign: 1 | -1) {
    const ms = parseOffset(offset);
    if (ms === null) {
      setError('Enter a timecode (HH:MM:SS,mmm) or a number of milliseconds.');
      return;
    }
    setError(null);
    onApply(
      shiftCues(doc, sign * ms),
      `Shifted ${cueCount(doc)} ${ms} ms ${sign > 0 ? 'later' : 'earlier'}.`,
    );
    track('timing_tool', { tool: 'shift' });
  }

  return (
    <Tool title="Shift" desc="Move every cue earlier or later by a fixed offset.">
      <div className="cf-timing__row">
        <label className="cf-field">
          <span>Offset</span>
          <input
            className="cf-input"
            aria-label="Shift offset"
            value={offset}
            onChange={(e) => setOffset(e.target.value)}
          />
        </label>
        <button type="button" className="cf-btn" onClick={() => apply(-1)}>
          Earlier
        </button>
        <button type="button" className="cf-btn" onClick={() => apply(1)}>
          Later
        </button>
      </div>
      {error && (
        <p className="cf-timing__error" role="alert">
          {error}
        </p>
      )}
    </Tool>
  );
}

/** Two-anchor linear retime (free) — engine: linearScale. */
function RetimeTool({ doc, onApply }: ToolProps) {
  const firstStart = doc.cues[0]?.startMs ?? 0;
  const lastEnd = doc.cues[doc.cues.length - 1]?.endMs ?? 0;
  const [aFrom, setAFrom] = useState(formatTimecode(firstStart));
  const [aTo, setATo] = useState(formatTimecode(firstStart));
  const [bFrom, setBFrom] = useState(formatTimecode(lastEnd));
  const [bTo, setBTo] = useState(formatTimecode(lastEnd));
  const [error, setError] = useState<string | null>(null);

  function apply() {
    const a = { fromMs: parseTimecode(aFrom), toMs: parseTimecode(aTo) };
    const b = { fromMs: parseTimecode(bFrom), toMs: parseTimecode(bTo) };
    if ([a.fromMs, a.toMs, b.fromMs, b.toMs].some((v) => v === null)) {
      setError('All four anchor times must be valid timecodes (HH:MM:SS,mmm).');
      return;
    }
    if (a.fromMs === b.fromMs) {
      setError('The two anchors need different source times.');
      return;
    }
    setError(null);
    onApply(linearScale(doc, a as Anchor, b as Anchor), `Retimed ${cueCount(doc)} linearly.`);
    track('timing_tool', { tool: 'linear' });
  }

  return (
    <Tool title="Linear retime" desc="Stretch or compress timing between two time anchors.">
      <div className="cf-timing__anchors">
        <AnchorFields legend="Anchor A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} />
        <AnchorFields legend="Anchor B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} />
      </div>
      <button type="button" className="cf-btn" onClick={apply}>
        Apply retime
      </button>
      {error && (
        <p className="cf-timing__error" role="alert">
          {error}
        </p>
      )}
    </Tool>
  );
}

/** Drift correction (Pro) from two confirmed sync points — engine: driftFromSyncPoints. */
function DriftTool({ doc, onApply, isPro }: ToolProps & { isPro: boolean }) {
  const firstStart = doc.cues[0]?.startMs ?? 0;
  const lastEnd = doc.cues[doc.cues.length - 1]?.endMs ?? 0;
  const [aIn, setAIn] = useState(formatTimecode(firstStart));
  const [aShould, setAShould] = useState(formatTimecode(firstStart));
  const [bIn, setBIn] = useState(formatTimecode(lastEnd));
  const [bShould, setBShould] = useState(formatTimecode(lastEnd));
  const [error, setError] = useState<string | null>(null);

  const correction = useMemo(() => {
    const aAt = parseTimecode(aIn);
    const aShouldMs = parseTimecode(aShould);
    const bAt = parseTimecode(bIn);
    const bShouldMs = parseTimecode(bShould);
    if (aAt === null || aShouldMs === null || bAt === null || bShouldMs === null || aAt === bAt) {
      return null;
    }
    return driftFromSyncPoints(
      { atMs: aAt, shouldBeMs: aShouldMs },
      { atMs: bAt, shouldBeMs: bShouldMs },
    );
  }, [aIn, aShould, bIn, bShould]);

  function apply() {
    if (!correction) {
      setError('Enter two distinct sync points — each as where the cue is and where it should be.');
      return;
    }
    setError(null);
    onApply(
      linearScale(doc, correction.anchors[0], correction.anchors[1]),
      `Applied drift correction (${(correction.rate * 100).toFixed(2)}% rate) to ${cueCount(doc)}.`,
    );
    track('timing_tool', { tool: 'drift' });
  }

  return (
    <Tool
      title="Drift correction"
      desc="Mark two cues' real and correct times to remove gradual drift (fps drift: use frame-rate conversion)."
      locked={!isPro}
    >
      {isPro && (
        <>
          <div className="cf-timing__anchors">
            <AnchorFields
              legend="Point A"
              from={aIn}
              to={aShould}
              onFrom={setAIn}
              onTo={setAShould}
            />
            <AnchorFields
              legend="Point B"
              from={bIn}
              to={bShould}
              onFrom={setBIn}
              onTo={setBShould}
            />
          </div>
          {correction && (
            <p className="cf-timing__preview">
              Suggested correction: {(correction.rate * 100).toFixed(2)}% rate (
              {driftRateLabel(correction.driftMsPerMinute, doc.metadata.frameRate)}).
            </p>
          )}
          <button type="button" className="cf-btn" onClick={apply}>
            Apply correction
          </button>
          {error && (
            <p className="cf-timing__error" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </Tool>
  );
}

const FPS_OPTIONS = STANDARD_FPS as readonly number[];

/** Frame-rate conversion (Pro) — engine: convertFrameRate (pure ms rescaling). */
function FrameRateTool({ doc, onApply, isPro }: ToolProps & { isPro: boolean }) {
  const declared = doc.metadata.frameRate;
  const sourceDefault = declared !== undefined && FPS_OPTIONS.includes(declared) ? declared : 25;
  const [from, setFrom] = useState(String(sourceDefault));
  const [to, setTo] = useState(
    String(FPS_OPTIONS.find((f) => f !== sourceDefault) ?? sourceDefault),
  );
  // Same source and target would "apply" a no-op — block it with a reason
  // (cue-forge-w28.13, drt.15) instead of silently pretending to convert.
  const sameFps = from === to;

  return (
    <Tool
      title="Frame-rate conversion"
      desc="Convert between 23.976 / 24 / 25 / 29.97 / 30 fps."
      locked={!isPro}
    >
      {isPro && (
        <>
          <div className="cf-timing__row">
            <FpsSelect label="Source frame rate" value={from} onChange={setFrom} />
            <FpsSelect label="Target frame rate" value={to} onChange={setTo} />
            <button
              type="button"
              className="cf-btn"
              disabled={sameFps}
              onClick={() => {
                onApply(
                  convertFrameRate(doc, Number(from), Number(to)),
                  `Converted ${from} fps to ${to} fps.`,
                );
                track('timing_tool', { tool: 'framerate' });
              }}
            >
              Convert frame rate
            </button>
          </div>
          {sameFps && (
            <p className="cf-timing__hint">
              Source and target frame rates are the same — choose two different rates to convert.
            </p>
          )}
        </>
      )}
    </Tool>
  );
}

function FpsSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="cf-field">
      <span>{label}</span>
      <select
        className="cf-select"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {FPS_OPTIONS.map((fps) => (
          <option key={fps} value={fps}>
            {fps}
          </option>
        ))}
      </select>
    </label>
  );
}

function AnchorFields({
  legend,
  from,
  to,
  onFrom,
  onTo,
}: {
  legend: string;
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  return (
    <fieldset className="cf-timing__anchor">
      <legend>{legend}</legend>
      <label className="cf-field">
        <span>Source</span>
        <input
          className="cf-input"
          aria-label={`${legend} source`}
          value={from}
          onChange={(e) => onFrom(e.target.value)}
        />
      </label>
      <label className="cf-field">
        <span>Target</span>
        <input
          className="cf-input"
          aria-label={`${legend} target`}
          value={to}
          onChange={(e) => onTo(e.target.value)}
        />
      </label>
    </fieldset>
  );
}

/** Human-readable summary of a proposed resync. */
function describeProposal(r: AlignmentResult): string {
  if (r.anchors) return `linear retime (rate ${r.rate.toFixed(3)})`;
  if (r.shiftMs === 0) return 'already in sync';
  return `shift ${r.shiftMs > 0 ? 'later' : 'earlier'} by ${formatTimecode(Math.abs(r.shiftMs))}`;
}

/**
 * Auto-resync (Pro, headline) — attach a local audio/video file, run the
 * alignment engine off-thread via the injected runner, review the proposed
 * offset/rate, and apply it. The file is decoded on-device and never uploaded.
 */
function AutoResyncTool({
  doc,
  onApply,
  isPro,
  runner,
}: ToolProps & { isPro: boolean; runner: ResyncRunner }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [proposal, setProposal] = useState<AlignmentResult | null>(null);
  const [estimateRate, setEstimateRate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onFile(file: File) {
    setError(null);
    setProposal(null);
    setAnalyzing(true);
    try {
      setProposal(await runner(doc, file, { estimateRate }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not analyse the audio.');
    } finally {
      setAnalyzing(false);
    }
  }

  return (
    <Tool
      title="Auto-resync"
      desc="Attach a local audio or video file and realign the subtitles to its speech."
      locked={!isPro}
    >
      {isPro && (
        <>
          <p className="cf-timing__privacy">
            The file is decoded on your device — nothing is uploaded.
          </p>
          <div className="cf-timing__row">
            <label className="cf-field">
              <span>Audio or video</span>
              <input
                className="cf-input"
                type="file"
                aria-label="Attach audio or video"
                accept="audio/*,video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                  e.target.value = '';
                }}
              />
            </label>
            <label className="cf-timing__check">
              <input
                type="checkbox"
                checked={estimateRate}
                onChange={(e) => setEstimateRate(e.target.checked)}
              />
              Also correct drift (rate)
            </label>
          </div>
          {analyzing && (
            <p className="cf-timing__preview" role="status">
              Analysing audio…
            </p>
          )}
          {proposal && (
            <div className="cf-timing__preview">
              <p>
                Suggested resync: {describeProposal(proposal)} — confidence{' '}
                {Math.round(proposal.score * 100)}%.
              </p>
              <button
                type="button"
                className="cf-btn"
                onClick={() => {
                  onApply(
                    applyResync(doc, proposal),
                    `Applied resync — ${describeProposal(proposal)}.`,
                  );
                  track('timing_tool', { tool: 'resync' });
                }}
              >
                Apply resync
              </button>
            </div>
          )}
          {error && (
            <p className="cf-timing__error" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </Tool>
  );
}

/**
 * One timing tool: a labelled section with a description and optional controls.
 * `locked` marks a Pro tool a free user cannot use yet (lock badge; the shared
 * UpgradePrompt provides the activation path).
 */
function Tool({
  title,
  desc,
  locked = false,
  children,
}: {
  title: string;
  desc: string;
  locked?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <section
      className={`cf-timing__tool${locked ? ' cf-timing__tool--locked' : ''}`}
      aria-label={title}
    >
      <h2 className="cf-timing__tool-title">
        {title}
        {locked && (
          <>
            {' '}
            <ProLock />
          </>
        )}
      </h2>
      <p className="cf-timing__tool-desc">{desc}</p>
      {children}
    </section>
  );
}
